import { z } from 'zod';
/**
 * Event Types - All possible event types in the system
 */
export const EventTypeSchema = z.enum([
    'page_view',
    'content_click',
    'engagement',
    'conversion',
    'search',
    'form_interaction',
    'error',
    'custom',
    'test_event'
]);
/**
 * Metadata - mix of frontend-provided and backend-enriched fields
 * Frontend provides: is_test, test_run_id, test_id
 * Backend enriches with: user_agent, ip_address, referer, environment
 */
export const MetadataSchema = z.object({
    // Frontend-provided test markers
    is_test: z.boolean().optional(),
    test_run_id: z.string().optional(),
    test_id: z.string().optional(),
    // Backend-enriched fields
    user_agent: z.string().optional(),
    ip_address: z.string().optional(),
    referer: z.string().optional(),
    environment: z.string().optional(),
});
/**
 * Page View Event
 */
export const PageViewDataSchema = z.object({
    page_path: z.string(),
    page_title: z.string().optional(),
    page_location: z.string().optional(),
    page_referrer: z.string().optional(),
}).passthrough();
export const PageViewEventSchema = z.object({
    event_type: z.literal('page_view'),
    data: PageViewDataSchema,
    user_id: z.string().optional(),
    session_id: z.string().optional(),
    metadata: MetadataSchema.partial().optional(),
});
/**
 * Content Click Event
 */
export const ContentClickDataSchema = z.object({
    content_category: z.string(),
    content_id: z.string(),
    content_name: z.string(),
}).passthrough();
export const ContentClickEventSchema = z.object({
    event_type: z.literal('content_click'),
    data: ContentClickDataSchema,
    user_id: z.string().optional(),
    session_id: z.string().optional(),
    metadata: MetadataSchema.partial().optional(),
});
/**
 * Engagement Event
 */
export const EngagementDataSchema = z.object({
    engagement_action: z.string(),
    engagement_category: z.string(),
}).passthrough();
export const EngagementEventSchema = z.object({
    event_type: z.literal('engagement'),
    data: EngagementDataSchema,
    user_id: z.string().optional(),
    session_id: z.string().optional(),
    metadata: MetadataSchema.partial().optional(),
});
/**
 * Conversion Event
 */
export const ConversionDataSchema = z.object({
    conversion_type: z.string(),
    conversion_value: z.number().optional(),
    currency: z.string().optional(),
    conversion_id: z.string().optional(),
}).passthrough();
export const ConversionEventSchema = z.object({
    event_type: z.literal('conversion'),
    data: ConversionDataSchema,
    user_id: z.string().optional(),
    session_id: z.string().optional(),
    metadata: MetadataSchema.partial().optional(),
});
/**
 * Search Event
 */
export const SearchDataSchema = z.object({
    search_term: z.string(),
    search_results: z.number().optional(),
}).passthrough();
export const SearchEventSchema = z.object({
    event_type: z.literal('search'),
    data: SearchDataSchema,
    user_id: z.string().optional(),
    session_id: z.string().optional(),
    metadata: MetadataSchema.partial().optional(),
});
/**
 * Form Interaction Event
 */
export const FormInteractionDataSchema = z.object({
    form_name: z.string(),
    form_action: z.string(),
}).passthrough();
export const FormInteractionEventSchema = z.object({
    event_type: z.literal('form_interaction'),
    data: FormInteractionDataSchema,
    user_id: z.string().optional(),
    session_id: z.string().optional(),
    metadata: MetadataSchema.partial().optional(),
});
/**
 * Error Event
 */
export const ErrorDataSchema = z.object({
    error_type: z.string(),
    error_message: z.string(),
    page_path: z.string().optional(),
}).passthrough();
export const ErrorEventSchema = z.object({
    event_type: z.literal('error'),
    data: ErrorDataSchema,
    user_id: z.string().optional(),
    session_id: z.string().optional(),
    metadata: MetadataSchema.partial().optional(),
});
/**
 * Custom Event (flexible schema)
 */
export const CustomDataSchema = z.record(z.unknown());
export const CustomEventSchema = z.object({
    event_type: z.literal('custom'),
    data: CustomDataSchema,
    user_id: z.string().optional(),
    session_id: z.string().optional(),
    metadata: MetadataSchema.partial().optional(),
});
/**
 * Test Event (for testing purposes)
 */
export const TestEventSchema = z.object({
    event_type: z.literal('test_event'),
    data: z.record(z.unknown()),
    user_id: z.string().optional(),
    session_id: z.string().optional(),
    metadata: MetadataSchema.partial().optional(),
});
/**
 * Union of all event request types (what the frontend sends)
 */
export const EventRequestSchema = z.discriminatedUnion('event_type', [
    PageViewEventSchema,
    ContentClickEventSchema,
    EngagementEventSchema,
    ConversionEventSchema,
    SearchEventSchema,
    FormInteractionEventSchema,
    ErrorEventSchema,
    CustomEventSchema,
    TestEventSchema,
]).refine((event) => {
    // If this is a test event, metadata with test_run_id and test_id is required
    if (event.metadata?.is_test === true) {
        return !!(event.metadata.test_run_id && event.metadata.test_id);
    }
    return true;
}, {
    message: 'metadata.test_run_id and metadata.test_id are required when metadata.is_test=true',
});
/**
 * App Event (what the backend stores after enrichment)
 */
export const AppEventSchema = z.object({
    event_id: z.string(),
    event_type: EventTypeSchema,
    timestamp: z.number(),
    user_id: z.string(),
    session_id: z.string().optional(),
    data: z.record(z.unknown()), // Keep flexible for querying
    metadata: MetadataSchema,
});
/**
 * Query API Request Parameters
 * Query parameters for GET /events/query endpoint
 */
export const EventQueryParamsSchema = z.object({
    /** Event type to query (required) */
    event_type: z.string(),
    /** Filter by a specific data field key */
    filter_key: z.string().optional(),
    /** Filter by a specific data field value (requires filter_key) */
    filter_value: z.string().optional(),
    /** Filter by test marker (true/false) */
    is_test: z.string().optional(),
    /** Filter by test run ID (REQUIRED when is_test='true') */
    test_run_id: z.string().optional(),
    /** Limit number of results (max 100) */
    limit: z.string().optional(),
}).refine((data) => {
    // If querying test events, test_run_id is required
    if (data.is_test === 'true') {
        return !!data.test_run_id;
    }
    return true;
}, {
    message: 'test_run_id is required when is_test=true',
    path: ['test_run_id'],
});
